import sanitizeHtml from 'sanitize-html'
import legacyArticleSlugs from '../data/legacy-article-slugs.json'
import { displayImage } from './image'

const THETORAH_HOSTS = new Set(['thetorah.com', 'www.thetorah.com'])

/** Pre-migration slug → current slug, from `legacy-article-slugs.json`. */
const LEGACY_ARTICLE_SLUGS: { renames: Record<string, string>; missing: string[] } =
	legacyArticleSlugs

/** Legacy slugs the CMS no longer serves at all — their links go to search. */
const LEGACY_MISSING_SLUGS = new Set(LEGACY_ARTICLE_SLUGS.missing)

/**
 * Legacy first path segments → the route the current app serves.
 * The old site exposed articles at the document root (`/sukkah-and-its-symbolism/`),
 * served blog posts under `/blogs/`, and used `/interview/` + `/op-ed/` for its
 * essay templates (see `public/_redirects`).
 */
const LEGACY_PREFIXES: Record<string, string> = {
	blogs: 'blog',
	blog: 'blog',
	books: 'book',
	book: 'book',
	category: 'categories',
	categories: 'categories',
	holiday: 'holidays',
	holidays: 'holidays',
	interview: 'article',
	'op-ed': 'article',
	article: 'article',
	// Long-form essays were also served at the root; the app keeps them under /article.
	essay: 'article',
	essays: 'article',
}

/** Top-level legacy URLs that are not articles and map to a real page. */
const LEGACY_ROOT_ALIASES: Record<string, string> = {
	donation: '/donate',
	'contact-us': '/contact',
	'terms-of-use': '/terms',
}

/** Top-level segments that are already real app routes and must not be rewritten. */
const APP_ROUTE_SEGMENTS = new Set([
	'article',
	'blog',
	'author',
	'authors',
	'topic',
	'topics',
	'parashah',
	'parashiyot',
	'series',
	'symposium',
	'categories',
	'holidays',
	'book',
	'about',
	'contact',
	'donate',
	'search',
	'store',
	'podcast',
	'videos',
	'events',
	'print',
	'rss.xml',
	'favicon.ico',
])

/**
 * Rewrite a link that points at TheTorah.com into an app-relative one.
 *
 * Migrated articles still carry the pre-migration absolute URLs
 * (`http://thetorah.com/<slug>/`, `https://www.thetorah.com/blogs/…`). Left as-is
 * they leave the SPA for the live site (which 404s on most of those legacy slugs).
 * Returning a relative path keeps navigation inside the app and in the same tab;
 * external hosts are returned untouched.
 */
export function normalizeInternalHref(value: string): string {
	const href = (value ?? '').trim()
	if (!href) return href
	// In-page anchors, tel:, mailto: and already-relative paths stay as they are.
	if (!/^(?:[a-z][a-z0-9+.-]*:)?\/\//i.test(href)) return href

	let url: URL
	try {
		url = new URL(href.startsWith('//') ? `https:${href}` : href)
	} catch {
		return href
	}
	if (!THETORAH_HOSTS.has(url.hostname.toLowerCase())) return href

	const segments = url.pathname.split('/').filter(Boolean)
	const suffix = `${url.search}${url.hash}`
	if (!segments.length) return `/${suffix}`

	const [first, ...rest] = segments
	const normalizedFirst = LEGACY_PREFIXES[first.toLowerCase()] ?? first

	if (APP_ROUTE_SEGMENTS.has(normalizedFirst.toLowerCase())) {
		// A legacy `/article/<pre-migration-slug>` is still a stale slug: the CMS
		// re-slugged a large part of the archive, so passing it through 404s (and the
		// SSG validator rejects pages that link to a redirect). Resolve it like the
		// root-form URLs below.
		if (normalizedFirst.toLowerCase() === 'article' && rest.length === 1) {
			return `${resolveLegacyArticlePath(rest[0])}${suffix}`
		}
		return `/${[normalizedFirst, ...rest].join('/')}${suffix}`
	}

	// Single-segment legacy pages that were never articles (`/donation/`).
	if (!rest.length) {
		const alias = LEGACY_ROOT_ALIASES[first.toLowerCase()]
		if (alias) return `${alias}${suffix}`
	}

	// Bare legacy slug (`/sukkah-and-its-symbolism/`) — article pages used to sit at
	// the root. Multi-segment leftovers (`/series-name/post/`) resolve on their tail.
	const slug = rest.length ? segments[segments.length - 1] : first
	return `${resolveLegacyArticlePath(slug)}${suffix}`
}

/**
 * Resolve a legacy article slug to a route the CMS actually serves.
 *
 * The migration re-slugged a large part of the archive (`sukkah-and-its-symbolism`
 * → `the-sukkah-and-its-symbolism`, retitled essays, …), so rewriting a legacy URL
 * to `/article/<legacy-slug>` alone still 404s. `legacy-article-slugs.json`
 * (generated from the migration snapshot) carries the rename table; slugs that no
 * longer exist at all fall back to search instead of a dead end.
 */
export function resolveLegacyArticlePath(slug: string): string {
	const key = (slug ?? '').trim().toLowerCase()
	if (!key) return '/article'
	const renamed = LEGACY_ARTICLE_SLUGS.renames[key]
	if (renamed) return `/article/${renamed}`
	if (LEGACY_MISSING_SLUGS.has(key)) {
		const query = key.split('-').filter(Boolean).join(' ')
		return `/search?q=${encodeURIComponent(query)}`
	}
	return `/article/${slug}`
}

/** True when `href` points back into this site (relative or a TheTorah.com URL). */
export function isInternalHref(value: string): boolean {
	const href = (value ?? '').trim()
	if (!href) return false
	if (href.startsWith('/') || href.startsWith('#')) return true
	try {
		const url = new URL(href.startsWith('//') ? `https:${href}` : href)
		return THETORAH_HOSTS.has(url.hostname.toLowerCase())
	} catch {
		return false
	}
}

// Parser-based and DOM-independent: the same policy runs during SSG and hydration.
// Existing migrated articles embed YouTube and Vimeo; no other frame origins are allowed.
export function sanitizeArticleHtml(value: unknown): string {
	return sanitizeHtml(typeof value === 'string' ? value : '', {
		allowedTags: [...sanitizeHtml.defaults.allowedTags, 'img', 'figure', 'figcaption', 'iframe', 'video', 'audio', 'source'],
		allowedAttributes: {
			'*': ['class', 'id', 'dir', 'lang', 'title'],
			p: ['style'],
			blockquote: ['style'],
			li: ['style'],
			a: ['href', 'name', 'target', 'rel'],
			img: ['src', 'alt', 'width', 'height', 'loading', 'decoding'],
			iframe: ['src', 'width', 'height', 'title', 'allowfullscreen', 'loading'],
			video: ['src', 'poster', 'controls', 'width', 'height', 'preload'],
			audio: ['src', 'controls', 'preload'],
			source: ['src', 'type'],
			td: ['colspan', 'rowspan'],
			th: ['colspan', 'rowspan', 'scope'],
		},
		allowedSchemes: ['https', 'http', 'mailto', 'tel'],
		allowedSchemesByTag: { iframe: ['https'] },
		allowProtocolRelative: false,
		allowedIframeHostnames: ['www.youtube.com', 'www.youtube-nocookie.com', 'player.vimeo.com'],
		allowedStyles: {
			'*': {
				'padding-left': [/^\d+(?:\.\d+)?(?:px|em|rem)$/],
				'padding-inline-start': [/^\d+(?:\.\d+)?(?:px|em|rem)$/],
			},
		},
		allowIframeRelativeUrls: false,
		transformTags: {
			img: (_tagName, attribs) => ({
				tagName: 'img',
				attribs: {
					...attribs,
					src: displayImage(attribs.src, 'content'),
					loading: attribs.loading || 'lazy',
					decoding: 'async',
				},
			}),
			iframe: (_tagName, attribs) => ({
				tagName: 'iframe',
				attribs: { ...attribs, src: attribs.src?.startsWith('//') ? `https:${attribs.src}` : attribs.src || '' },
			}),
			a: (_tagName, attribs) => {
				const href = attribs.href || ''
				const internal = isInternalHref(href)
				const next = { ...attribs }
				if (href) next.href = normalizeInternalHref(href)
				if (internal) {
					delete next.target
					delete next.rel
				}
				else if (next.target === '_blank') next.rel = 'noopener noreferrer'
				return { tagName: 'a', attribs: next }
			},
		},
	})
}

/** Titles are stored as a single rich-text paragraph. Keep inline markup, drop the wrapper. */
export function sanitizeArticleTitleHtml(value: unknown): string {
	const html = sanitizeArticleHtml(value).trim()
	const wrapped = html.match(/^<p\b[^>]*>([\s\S]*)<\/p>$/i)
	return (wrapped && !/<\/p>/i.test(wrapped[1]) ? wrapped[1] : html).trim()
}

export function articleTitleText(value: unknown): string {
	return sanitizeArticleTitleHtml(value).replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
}

export function sanitizeFootnotes(value: string | Array<{ id: number; text: string }> | undefined) {
	return Array.isArray(value)
		? value.map(note => ({ ...note, text: sanitizeArticleHtml(note.text) }))
		: sanitizeArticleHtml(value)
}

export function articleHtmlFields(record: Record<string, unknown>) {
	return {
		subtitle: sanitizeArticleHtml(record.teaser || record.subtitle),
		imageCaption: sanitizeArticleHtml(record.caption || record.imageCaption),
		intervieweeBio: sanitizeArticleHtml(record.intervieweeBio),
		content: sanitizeArticleHtml(record.content),
	}
}
